import { useState, useCallback } from "react";
import { Form, message } from "antd";
import { CuerpoDocumentoItem, FacturaFormValues } from "../types/facturaTypes";
import { parseNumberToFixed2 } from "../../../utils/ParseNumer/parseNumber2Digits";
import { calcularIVADesdeBase } from "../../../utils/ivaCalculator";
import { enviarFacturaElectronica } from "../../../api/DTEs/facturaElectronica.dtes";
import type { FacturaElectronicaRequest } from "../../../api/DTEs/types/dtes.types";
import { useCatalogos } from "./useCatalogos";
import {
  formatNumeroDocumento,
  detectTipoDocumento,
  TipoDocumentoCode,
  toReceptorTipoDocumento,
} from "../../../api/Catalogos/types/CAT-022.types";
import { formatDocumento } from "../utils/duiFormatter";

// Interface para datos prellenados del pago
interface PrefilledPaymentData {
  amortizationId: number;
  transactionId: string;
  monto: number;
  provider: string;
}

export const useFacturaForm = () => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const {
    catalogos,
    loading: catalogosLoading,
    getMunicipiosByDepartamento,
  } = useCatalogos();
  const [selectedDepartamento, setSelectedDepartamento] = useState<
    number | null
  >(null);
  const [showResumenModal, setShowResumenModal] = useState(false);
  const [resumenData, setResumenData] = useState<FacturaFormValues | null>(
    null
  );
  const [prefilledData, setPrefilledData] =
    useState<PrefilledPaymentData | null>(null);

  const [cuerpoDocumento, setCuerpoDocumento] = useState<CuerpoDocumentoItem[]>(
    [
      {
        key: 1,
        cantidad: 1,
        codigo: "",
        uniMedida: 1,
        descripcion: "",
        precioUni: 0,
        montoDescu: 0,
        ventaGravada: 0,
        noGravado: 0,
        tipoItem: 1,
        ventaNoSuj: 0,
        ventaExenta: 0,
        ivaItem: 0,
        numeroDocumento: "",
        codTributo: "20", // IVA 13% por defecto
        tributos: [],
        psv: 0,
      },
    ]
  );

  const addItem = () => {
    const newItem: CuerpoDocumentoItem = {
      key: Date.now(),
      cantidad: 1,
      codigo: "",
      uniMedida: 1,
      descripcion: "",
      precioUni: 0,
      montoDescu: 0,
      ventaGravada: 0,
      noGravado: 0,
      tipoItem: 1,
      ventaNoSuj: 0,
      ventaExenta: 0,
      ivaItem: 0,
      numeroDocumento: "",
      codTributo: "20", // IVA 13% por defecto
      tributos: [],
      psv: 0,
    };
    setCuerpoDocumento([...cuerpoDocumento, newItem]);
  };

  const addItemWithData = (itemData: Partial<CuerpoDocumentoItem>) => {
    const newItem: CuerpoDocumentoItem = {
      key: Date.now(),
      cantidad: 1,
      codigo: "",
      uniMedida: 1,
      descripcion: "",
      precioUni: 0,
      montoDescu: 0,
      ventaGravada: 0,
      noGravado: 0,
      tipoItem: 1,
      ventaNoSuj: 0,
      ventaExenta: 0,
      ivaItem: 0,
      numeroDocumento: "",
      codTributo: "20", // IVA 13% por defecto
      tributos: [],
      psv: 0,
      ...itemData,
    };
    setCuerpoDocumento([...cuerpoDocumento, newItem]);
  };

  const removeItem = (key: number) => {
    setCuerpoDocumento(cuerpoDocumento.filter((item) => item.key !== key));
  };

  const updateItem = (
    key: number,
    field: string,
    value: string | number | string[]
  ) => {
    setCuerpoDocumento(
      cuerpoDocumento.map((item) => {
        if (item.key === key) {
          const updatedItem = { ...item, [field]: value };

          // Auto-calculate IVA and other fields when relevant fields change
          if (["cantidad", "precioUni", "montoDescu"].includes(field)) {
            const subtotal = updatedItem.cantidad * updatedItem.precioUni;
            const afterDiscount = subtotal - (updatedItem.montoDescu || 0);
            updatedItem.ventaGravada = parseNumberToFixed2(afterDiscount);

            // CORRECCIÓN: Calcular IVA correctamente
            // afterDiscount es la base imponible (sin IVA), así que calculamos 13% sobre esa base
            const calculoIVA = calcularIVADesdeBase(afterDiscount);
            updatedItem.ivaItem = parseNumberToFixed2(calculoIVA.iva);
          }

          return updatedItem;
        }
        return item;
      })
    );
  };

  const resetForm = () => {
    form.resetFields();
    setCuerpoDocumento([
      {
        key: 1,
        cantidad: 1,
        codigo: "",
        uniMedida: 1,
        descripcion: "",
        precioUni: 0,
        montoDescu: 0,
        ventaGravada: 0,
        noGravado: 0,
        tipoItem: 1,
        ventaNoSuj: 0,
        ventaExenta: 0,
        ivaItem: 0,
        numeroDocumento: "",
        codTributo: "20", // IVA 13% por defecto
        tributos: [],
        psv: 0,
      },
    ]);
  };

  const handleSubmit = async (values: FacturaFormValues) => {
    // Validar que haya al menos un producto
    if (
      cuerpoDocumento.length === 0 ||
      cuerpoDocumento.every((item) => !item.descripcion)
    ) {
      message.error("Debe agregar al menos un producto o servicio");
      return;
    }

    // Validar campos requeridos
    if (
      !values.nit ||
      !values.receptor_nombre ||
      !values.receptor_numDocumento
    ) {
      message.error("Faltan campos obligatorios en el formulario");
      return;
    }

    try {
      // Guardar los datos y mostrar el modal de resumen
      setResumenData(values);
      setShowResumenModal(true);
    } catch (error) {
      console.error("Error al procesar el formulario:", error);
      message.error("Error al procesar los datos del formulario");
    }
  };

  const handleConfirmFactura = async () => {
    if (!resumenData) return;

    try {
      setLoading(true);

      const facturaData: FacturaElectronicaRequest = {
        nit: resumenData.nit,
        receptor: {
          nrc: null, // SIEMPRE null para consumidor final según correcciones aplicadas
          nombre: resumenData.receptor_nombre,
          codActividad: resumenData.receptor_codActividad,
          descActividad: resumenData.receptor_descActividad,
          direccion: {
            departamento: resumenData.receptor_departamento,
            municipio: resumenData.receptor_municipio,
            complemento: resumenData.receptor_complemento,
          },
          telefono: resumenData.receptor_telefono || null, // NULL en lugar de cadena vacía
          correo: resumenData.receptor_correo || null, // NULL en lugar de cadena vacía
          tipoDocumento: (() => {
            // Validar y convertir el tipo de documento usando CAT-022
            const tipoInput = resumenData.receptor_tipoDocumento;

            // Validar que sea uno de los valores permitidos exactamente
            if (tipoInput && ["13", "36", "37"].includes(tipoInput)) {
              return tipoInput as "13" | "36" | "37";
            }

            // Fallback: detectar automáticamente y mapear correctamente
            try {
              const tipoDetectado = detectTipoDocumento(
                resumenData.receptor_numDocumento
              );
              const tipoMapeado = toReceptorTipoDocumento(tipoDetectado);
              return tipoMapeado;
            } catch {
              return "13"; // DUI por defecto
            }
          })(),
          numDocumento: (() => {
            // Formatear el número de documento según el tipo
            try {
              const tipoDoc =
                resumenData.receptor_tipoDocumento as TipoDocumentoCode;
              return formatNumeroDocumento(
                resumenData.receptor_numDocumento,
                tipoDoc
              );
            } catch {
              // Fallback usando la función antigua
              return formatDocumento(
                resumenData.receptor_numDocumento,
                resumenData.receptor_tipoDocumento
              );
            }
          })(),
        },
        cuerpoDocumento: cuerpoDocumento.map((item, index) => ({
          numItem: index + 1, // Add the missing numItem property
          cantidad: item.cantidad,
          codigo: item.codigo,
          uniMedida: item.uniMedida,
          descripcion: item.descripcion,
          precioUni: parseNumberToFixed2(item.psv), // MODIFICADO: usar PSV (precio con IVA) en lugar de precioUni
          montoDescu: parseNumberToFixed2(item.montoDescu),
          ventaGravada: parseNumberToFixed2(item.ventaGravada),
          noGravado: parseNumberToFixed2(item.noGravado),
          tipoItem: 1, // FORZAR tipoItem: 1 para servicios según correcciones aplicadas
          ventaNoSuj: parseNumberToFixed2(item.ventaNoSuj),
          ventaExenta: parseNumberToFixed2(item.ventaExenta),
          ivaItem: parseNumberToFixed2(item.ivaItem),
          numeroDocumento: null, // SIEMPRE null según correcciones aplicadas
          codTributo: null, // SIEMPRE null para evitar conflictos según correcciones aplicadas
          tributos: null, // SIEMPRE null para evitar conflictos según correcciones aplicadas
          psv: parseNumberToFixed2(item.psv),
        })),
        documentoRelacionado:
          resumenData.documentoRelacionado &&
          resumenData.documentoRelacionado.length > 0
            ? resumenData.documentoRelacionado
            : null,
        otrosDocumentos:
          resumenData.otrosDocumentos && resumenData.otrosDocumentos.length > 0
            ? resumenData.otrosDocumentos
            : null,
        ventaTercero:
          resumenData.ventaTercero &&
          Object.keys(resumenData.ventaTercero).length > 0
            ? resumenData.ventaTercero
            : null,
        resumen: {
          descuNoSuj: parseNumberToFixed2(resumenData.resumen_descuNoSuj || 0),
          descuExenta: parseNumberToFixed2(
            resumenData.resumen_descuExenta || 0
          ),
          descuGravada: parseNumberToFixed2(
            resumenData.resumen_descuGravada || 0
          ),
          porcentajeDescuento: parseNumberToFixed2(
            resumenData.resumen_porcentajeDescuento || 0
          ),
          ivaRete1: parseNumberToFixed2(resumenData.resumen_ivaRete1 || 0),
          reteRenta: parseNumberToFixed2(resumenData.resumen_reteRenta || 0),
          saldoFavor: parseNumberToFixed2(resumenData.resumen_saldoFavor || 0),
          condicionOperacion: resumenData.resumen_condicionOperacion || 1,
          tributos:
            resumenData.resumen_tributos &&
            resumenData.resumen_tributos.length > 0
              ? resumenData.resumen_tributos
              : [],
          // CRÍTICO: pagos nunca puede estar vacío, debe tener al menos un elemento
          pagos:
            resumenData.resumen_pagos && resumenData.resumen_pagos.length > 0
              ? resumenData.resumen_pagos
              : [
                  {
                    codigo: "01", // Efectivo por defecto
                    montoPago: cuerpoDocumento.reduce(
                      (total, item) => total + item.cantidad * item.precioUni,
                      0
                    ),
                    referencia: `FACT-${Date.now()}`,
                    plazo: null,
                    periodo: null,
                  },
                ],
          numPagoElectronico: resumenData.resumen_numPagoElectronico || "",
          totalNoSuj: 0,
          totalExenta: 0,
          totalGravada: 0,
          subTotalVentas: 0,
          totalDescu: 0,
          subTotal: 0,
          montoTotalOperacion: 0,
          totalNoGravado: 0,
          totalPagar: 0,
          totalLetras: "",
          totalIva: 0,
        },
        extension: {
          nombEntrega: resumenData.extension_nombEntrega || null,
          docuEntrega: resumenData.extension_docuEntrega || null,
          nombRecibe: resumenData.extension_nombRecibe || null,
          docuRecibe: resumenData.extension_docuRecibe || null,
          observaciones: resumenData.extension_observaciones || null,
          placaVehiculo: resumenData.extension_placaVehiculo || null,
        },
        apendice:
          resumenData.apendice && resumenData.apendice.length > 0
            ? resumenData.apendice
            : null,
      };

      await enviarFacturaElectronica(facturaData);

      message.success("Factura electrónica enviada exitosamente");

      // Cerrar modal y resetear form
      setShowResumenModal(false);
      setResumenData(null);
      resetForm();
    } catch (error: unknown) {
      // Type guard para AxiosError
      const isAxiosError = (
        err: unknown
      ): err is {
        code?: string;
        message?: string;
        response?: {
          status?: number;
          statusText?: string;
          data?: { message?: string };
        };
        config?: { url?: string; method?: string; baseURL?: string };
      } => {
        return typeof err === "object" && err !== null;
      };

      if (isAxiosError(error)) {
        // Manejo específico de errores CORS y de red
        if (error.code === "ERR_NETWORK" && error.message === "Network Error") {
          message.error({
            content:
              "Error de conexión: No se puede conectar con el servidor de facturación. Verifique su conexión a internet o contacte al administrador.",
            duration: 8,
          });
        } else if (error.response?.status === 405) {
          message.error({
            content:
              "Método no permitido (405): El servidor no acepta este tipo de solicitud.",
            duration: 6,
          });
        } else if (error.response?.status === 401) {
          message.error({
            content:
              "No autorizado (401): Token de acceso inválido o expirado.",
            duration: 6,
          });
        } else if (error.response?.status === 400) {
          const errorData = error.response.data as
            | { message?: string; error?: string }
            | undefined;
          const errorMessage =
            errorData?.message ||
            errorData?.error ||
            "Datos de factura inválidos";

          // Identificar errores específicos comunes
          let specificMessage = errorMessage;
          if (errorMessage.includes("nrc") || errorMessage.includes("NRC")) {
            specificMessage =
              "Error en NRC: Debe enviarse como null para consumidor final, no como cadena vacía.";
          } else if (
            errorMessage.includes("pagos") ||
            errorMessage.includes("Pagos")
          ) {
            specificMessage =
              "Error en pagos: El arreglo pagos debe contener al menos un elemento, no puede estar vacío.";
          } else if (
            errorMessage.includes("tipoDocumento") ||
            errorMessage.includes("numDocumento")
          ) {
            specificMessage =
              'Error en documento: Verificar formato del DUI (########-#) y tipoDocumento debe ser "13".';
          } else if (
            errorMessage.includes("correo") ||
            errorMessage.includes("telefono")
          ) {
            specificMessage =
              "Error en contacto: Campos opcionales deben enviarse como null, no como cadenas vacías.";
          }

          message.error({
            content: `Error de validación de Hacienda: ${specificMessage}`,
            duration: 8,
          });
        } else if (error.response?.status && error.response.status >= 500) {
          message.error({
            content:
              "Error del servidor (500+): El servidor de facturación está experimentando problemas. Intente más tarde.",
            duration: 6,
          });
        } else {
          message.error({
            content: `Error al enviar la factura: ${
              error.message || "Error desconocido"
            }`,
            duration: 6,
          });
        }
      } else {
        message.error({
          content: "Error desconocido al enviar la factura.",
          duration: 6,
        });
      }
    } finally {
      setLoading(false);
    }
  };

  const handleDepartamentoChange = useCallback(
    (departamentoCode: string) => {
      // Buscar el departamento por código
      const departamento = catalogos.departamentos.find(
        (dept) => dept.value === departamentoCode
      );

      if (departamento) {
        // Usar el ID del departamento para filtrar municipios
        setSelectedDepartamento(departamento.id);
        form.setFieldValue("receptor_municipio", undefined);
      } else {
        setSelectedDepartamento(null);
        form.setFieldValue("receptor_municipio", undefined);
      }
    },
    [catalogos.departamentos, form]
  );

  const getFilteredMunicipios = useCallback(() => {
    if (!selectedDepartamento) return [];
    return getMunicipiosByDepartamento(selectedDepartamento);
  }, [selectedDepartamento, getMunicipiosByDepartamento]);

  return {
    form,
    loading: loading || catalogosLoading,
    cuerpoDocumento,
    catalogos,
    selectedDepartamento,
    addItem,
    addItemWithData,
    removeItem,
    updateItem,
    handleSubmit,
    handleConfirmFactura,
    resetForm,
    handleDepartamentoChange,
    getFilteredMunicipios,
    setCuerpoDocumento,
    showResumenModal,
    setShowResumenModal,
    resumenData,
    setResumenData,
    prefilledData,
    setPrefilledData,
  };
};
